import { Request } from 'api';
import { AxiosResponse } from 'axios';
import { ServiceTicket } from './service.types';

interface ServiceTicketsResponse {
    data: ServiceTicket[];
}

type ReturnTypeCollection = Promise<AxiosResponse<ServiceTicketsResponse>>;
type ReturnTypeSingle = Promise<AxiosResponse<{ data: ServiceTicket }>>;

/**
 * Get ticket counts for multiple organizations (batch endpoint)
 * Returns both total and open counts per organization
 */
export const getServiceTicketCounts = (
    organizationUuids: string[],
): Promise<
    AxiosResponse<{
        data: Record<
            string,
            {
                total: number;
                open: number;
            }
        >;
    }>
> => {
    return Request.get('/admin/service-tickets/counts', {
        params: {
            organization_uuids: organizationUuids,
        },
    });
};

/**
 * Get ticket counts grouped by category (open/closed)
 */
export const getServiceTicketStatusCounts = (): Promise<
    AxiosResponse<{
        data: {
            category_counts: {
                [key: string]: {
                    open: number;
                    closed: number;
                };
            };
            total_open: number;
            total_closed: number;
        };
    }>
> => {
    return Request.get('/admin/service-tickets/status-counts');
};

/**
 * Get all tickets for an organization
 */
export const getServiceTickets = (organizationUuid: string): ReturnTypeCollection => {
    return Request.get('/admin/service-tickets', {
        params: {
            organization_uuid: organizationUuid,
        },
    });
};

/**
 * Create a new service ticket
 */
export const createServiceTicket = (ticket: {
    organization_uuid: string;
    category: string;
    description: string;
    work_performed?: string | null;
    status: string;
}): ReturnTypeSingle => {
    return Request.post('/admin/service-tickets', ticket);
};

/**
 * Update a service ticket
 */
export const updateServiceTicket = (
    ticketUuid: string,
    updates: {
        category?: string;
        description?: string;
        work_performed?: string | null;
        status?: string;
    },
): ReturnTypeSingle => {
    return Request.put(`/admin/service-tickets/${ticketUuid}`, updates);
};
